import React from 'react';
import { Avatar } from './Avatar';
import { parseFormattedText, cn } from '@/lib/utils';
import { Undo2 } from 'lucide-react';
import type { RegularMessage } from '@/types';

interface MessageProps {
    message: RegularMessage;
    botAvatar: string;
    userAvatar: string;
    customStyles?: {
        primaryColor?: string;
        secondaryColor?: string;
        botMessageBg?: string;
        userMessageBg?: string;
        botMessageText?: string;
        userMessageText?: string;
        borderRadius?: number;
        fontSize?: number;
    };
    onUndo?: () => void;
}

export const Message: React.FC<MessageProps> = ({ message, botAvatar, userAvatar, customStyles, onUndo }) => {
    const isBot = message.sender === 'bot' || message.sender === 'assistant';
    const isUser = message.sender === 'user';

    // Get custom colors with fallbacks: bot bg = botMessageBg || secondaryColor, user bg = userMessageBg || primaryColor
    const userBgColor = customStyles?.userMessageBg || customStyles?.primaryColor || '#a855f7';
    const botBgColor = customStyles?.botMessageBg || customStyles?.secondaryColor || '#ffffff';
    const userTextColor = customStyles?.userMessageText ?? '#ffffff';
    const botTextColor = customStyles?.botMessageText ?? '#1f2937';
    const borderRadius = customStyles?.borderRadius || 16;
    const fontSize = customStyles?.fontSize ?? 14;

    return (
        <div
            className={cn(
                'zentri-flex zentri-items-end zentri-gap-3 zentri-mb-4 zentri-group',
                'zentri-animate-fade-in',
                isUser && 'zentri-flex-row-reverse'
            )}
        >
            {/* Avatar */}
            {isBot && <Avatar src={botAvatar} alt="Bot" size="md" />}
            {isUser && <Avatar src={userAvatar} alt="User" size="md" />}

            {/* Message bubble with undo button */}
            <div className="zentri-flex zentri-items-end zentri-gap-2" style={{ flexDirection: isUser ? 'row-reverse' : 'row' }}>
                {/* Message bubble */}
                <div
                    className={cn(
                        'zentri-px-3.5 zentri-py-2.5 zentri-max-w-md',
                        'zentri-transition-all zentri-duration-300 zentri-ease-out',
                        isUser
                            ? 'zentri-text-white zentri-border zentri-border-opacity-40'
                            : 'zentri-text-gray-800 zentri-border zentri-border-neutral-100'
                    )}
                    style={{
                        backgroundColor: isUser ? userBgColor : botBgColor,
                        borderRadius: `${borderRadius}px`,
                        borderTopLeftRadius: isUser ? `${borderRadius}px` : '0',
                        borderBottomRightRadius: isUser ? '0' : `${borderRadius}px`,
                        boxShadow: isUser
                            ? `0 4px 12px ${userBgColor}33`
                            : '0 4px 12px rgba(0, 0, 0, 0.08)',
                        borderColor: isUser ? `${userBgColor}66` : undefined,
                    }}
                >
                    {/* Message content */}
                    <div
                        className="zentri-leading-relaxed zentri-animate-fade-slide-up"
                        style={{
                            animationDelay: '0.2s',
                            color: isUser ? userTextColor : botTextColor,
                            fontSize: `${fontSize}px`,
                            ['--zentri-primary' as string]: customStyles?.primaryColor || '#a855f7',
                        }}
                        dangerouslySetInnerHTML={parseFormattedText(message.text)}
                    />
                </div>

                {/* Undo button - appears on hover for USER messages only */}
                {onUndo && isUser && (
                    <button
                        onClick={onUndo}
                        className="zentri-opacity-80 zentri-group-hover:zentri-opacity-100 zentri-transition-all zentri-duration-300 zentri-p-1.5 zentri-rounded-full zentri-text-white zentri-flex-shrink-0 hover:zentri-scale-110 zentri-shadow-md hover:zentri-shadow-lg"
                        style={{
                            color: userTextColor,
                            backgroundColor: userBgColor,
                            boxShadow: `0 2px 8px ${userBgColor}44`,
                        }}
                        title="Undo to this message"
                        aria-label="Undo to this message"
                    >
                        <Undo2 size={14} strokeWidth={2.5} />
                    </button>
                )}
            </div>
        </div>
    );
};
